'use client';

import { useState, useMemo, useEffect } from 'react';
import Link from 'next/link';
import {
  Package,
  CheckCircle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
} from 'lucide-react';
import Breadcrumbs from '@/components/Breadcrumbs';
import ReactMarkdown from 'react-markdown';
import type {
  ProductFamily,
  ProductVariant,
  ProductFeature,
  ProductUseCase,
  ProductFAQ
} from '@/lib/data/software';
import { VariantCards } from '@/components/software/VariantTable';
import SoftwareQuoteForm from '@/components/software/SoftwareQuoteForm';
import FeatureGrid from '@/components/software/FeatureGrid';
import UseCaseCards from '@/components/software/UseCaseCards';
import SocialProof from '@/components/software/SocialProof';
import { addToRecentlyViewed, RecentlyViewedCompact } from '@/components/software/RecentlyViewed';
import WhyInventiveHQ from '@/components/software/WhyInventiveHQ';
import DemoVideo from '@/components/software/DemoVideo';

/**
 * SoftwareProductLayout Props
 */
interface SoftwareProductLayoutProps {
  // Product data
  family: ProductFamily;
  features?: ProductFeature[];
  useCases?: ProductUseCase[];
  faqs?: ProductFAQ[];

  // Similar products (based on shared tags)
  similarProducts?: ProductFamily[];
}

/**
 * FAQ Item component with expand/collapse
 */
function FAQItem({
  faq,
  isOpen,
  onToggle
}: {
  faq: ProductFAQ;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full min-h-[48px] py-4 px-2 -mx-2 flex items-center justify-between text-left hover:bg-gray-50 transition-colors rounded-lg"
      >
        <span className="font-medium text-gray-900 pr-4 text-base">{faq.question}</span>
        {isOpen ? (
          <ChevronUp className="w-6 h-6 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-6 h-6 text-gray-500 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="pb-4 px-2 -mx-2">
          <p className="text-gray-600 whitespace-pre-wrap leading-relaxed text-base">{faq.answer}</p>
        </div>
      )}
    </div>
  );
}

/**
 * SoftwareProductLayout Component
 *
 * Standard layout for all software product pages on InventiveHQ.com
 * Structure: Hero → Features → Plans → Use Cases → FAQ → About → CTA → Related
 */
export default function SoftwareProductLayout({
  family,
  features = [],
  useCases = [],
  faqs = [],
  similarProducts = [],
}: SoftwareProductLayoutProps) {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const variants = family.variants || [];
  const hasMultipleVariants = variants.length > 1;

  // Selected variant for quote form
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(
    variants.length > 0 ? variants[0] : null
  );

  // Selected add-ons (multi-select)
  const [selectedAddonIds, setSelectedAddonIds] = useState<string[]>([]);

  const handleToggleAddon = (variant: ProductVariant) => {
    setSelectedAddonIds((prev) =>
      prev.includes(variant.id)
        ? prev.filter((id) => id !== variant.id)
        : [...prev, variant.id]
    );
  };

  const selectedAddonNames = variants
    .filter((v) => selectedAddonIds.includes(v.id))
    .map((v) => v.displayName);

  // Sticky CTA: show when form is scrolled out of view
  const [showStickyCta, setShowStickyCta] = useState(false);

  useEffect(() => {
    const formSection = document.getElementById('inquiry-form');
    if (!formSection) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Show sticky CTA when form section is NOT visible
        setShowStickyCta(!entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    observer.observe(formSection);
    return () => observer.disconnect();
  }, []);

  // Track product view for recently viewed
  useEffect(() => {
    addToRecentlyViewed({
      slug: family.slug,
      name: family.name,
      vendorName: family.vendorName,
      iconUrl: family.iconUrl || undefined,
    });
  }, [family.slug, family.name, family.vendorName, family.iconUrl]);

  // SEO metadata
  const siteUrl = 'https://inventivehq.com';
  const canonicalUrl = `${siteUrl}/software/${family.slug}`;
  const pageTitle = family.metaTitle || `${family.name} | ${family.vendorName} | Inventive HQ Software`;
  const pageDescription = family.metaDescription || family.tagline ||
    `Get ${family.name} from ${family.vendorName}. Expert deployment and dedicated support from Inventive HQ.`;

  // Build contact URL with tracking params
  const contactUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set('source', 'software-product');
    params.set('product', family.slug);
    return `/contact?${params.toString()}`;
  }, [family.slug]);

  // Breadcrumbs (Home is auto-prepended by Breadcrumbs component)
  const breadcrumbs = [
    { label: 'Software', href: '/software' },
    ...(family.category
      ? [{ label: family.category.name, href: `/software/category/${family.category.slug}` }]
      : []),
    { label: family.name }, // Current page - no href
  ];

  const scrollToForm = () => {
    document.getElementById('inquiry-form')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  // Generate FAQ Schema.org structured data
  const faqSchema = faqs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer.replace(/[#*`]/g, ''),
      },
    })),
  } : null;

  // Product Schema.org structured data
  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: family.name,
    description: pageDescription,
    brand: {
      '@type': 'Brand',
      name: family.vendorName,
    },
    url: canonicalUrl,
    ...(family.iconUrl && { image: family.iconUrl }),
  };

  // Note: BreadcrumbList schema is already handled by the Breadcrumbs component
  // Note: SEO meta tags are handled by generateMetadata in page.tsx (App Router pattern)

  return (
    <>
      {/* Structured Data - Product */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }}
      />

      {/* Structured Data - FAQ */}
      {faqSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}

      {/* Note: BreadcrumbList structured data is handled by the Breadcrumbs component */}

      <div className="min-h-screen bg-gray-50">
        {/* ===== 1. HERO SECTION ===== */}
        <section className="relative bg-gradient-to-br from-[#1a6f8f] to-[#1e40af] text-white py-10 sm:py-16 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Breadcrumbs items={breadcrumbs} className="mb-4 sm:mb-6 text-white opacity-90" />

            <Link
              href="/software"
              className="inline-flex items-center gap-2 text-white hover:text-white mb-4 sm:mb-6 transition-colors min-h-[44px] py-2"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-base">Back to Software Store</span>
            </Link>

            <div className="flex flex-col md:flex-row md:items-start gap-4 sm:gap-6">
              <div className="flex items-start gap-3 sm:gap-4 flex-1">
                {family.iconUrl ? (
                  <img
                    src={family.iconUrl}
                    alt={`${family.name} icon`}
                    loading="eager"
                    fetchPriority="high"
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl object-contain bg-white p-2 flex-shrink-0"
                  />
                ) : (
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl sm:text-3xl font-bold text-white">
                      {family.name.charAt(0)}
                    </span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white mb-1">{family.vendorName}</p>
                  <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 break-words">
                    {family.name}
                  </h1>
                  {family.tagline && (
                    <p className="text-base sm:text-lg text-white/90 max-w-2xl">{family.tagline}</p>
                  )}
                </div>
              </div>
              {/* Hero CTA */}
              <div className="flex flex-col sm:flex-row md:flex-col gap-3 md:min-w-[200px] mt-2 md:mt-0">
                <button
                  onClick={scrollToForm}
                  className="px-6 py-3.5 min-h-[48px] bg-amber-400 text-gray-900 font-bold rounded-lg hover:bg-amber-300 transition-colors shadow-md text-base"
                >
                  Get a Quote
                </button>
                <Link
                  href={contactUrl}
                  className="px-6 py-3.5 min-h-[48px] bg-transparent text-white font-medium rounded-lg border-2 border-white hover:bg-white hover:text-[#1e40af] transition-colors text-center text-base flex items-center justify-center"
                >
                  Talk to Sales
                </Link>
              </div>
            </div>

            {/* Trust Indicators - Glassmorphism Style */}
            <div className="mt-8 sm:mt-10">
              <div className="rounded-2xl border border-white/15 bg-white/10 px-4 sm:px-6 py-4 sm:py-5 backdrop-blur">
                <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center justify-center gap-x-4 sm:gap-x-8 gap-y-4 text-sm font-medium text-white/90">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#A7F3D0] flex-shrink-0" />
                    <span>Authorized Partner</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#A7F3D0] flex-shrink-0" />
                    <span>Expert Deployment</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#A7F3D0] flex-shrink-0" />
                    <span>Dedicated Support</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== 2. DEMO VIDEO (if available) ===== */}
        {variants.some(v => v.videoUrl) && (
          <section className="bg-white border-b">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                See {family.name} in Action
              </h2>
              <DemoVideo
                videoUrl={variants.find(v => v.videoUrl)?.videoUrl || ''}
                title={`${family.name} Demo`}
              />
            </div>
          </section>
        )}

        {/* ===== 3. KEY FEATURES (Full Width) ===== */}
        {features.length > 0 && (
          <section className="bg-white border-b">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Key Features</h2>
              <FeatureGrid features={features} />
            </div>
          </section>
        )}

        {/* ===== SOCIAL PROOF ===== */}
        {(family.g2Rating || family.capterraRating || family.customerCount || family.notableCustomers) && (
          <section className="bg-white border-b">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
              <SocialProof
                g2Rating={family.g2Rating}
                g2ReviewCount={family.g2ReviewCount}
                capterraRating={family.capterraRating}
                capterraReviewCount={family.capterraReviewCount}
                customerCount={family.customerCount}
                notableCustomers={family.notableCustomers}
              />
            </div>
          </section>
        )}

        {/* ===== MICROSOFT PRICING CALCULATOR BANNER ===== */}
        {family.vendorName === 'Microsoft' && ['microsoft-defender', 'microsoft-entra-id', 'microsoft-intune', 'microsoft-365', 'microsoft-purview'].includes(family.slug) && (
          <section className="bg-gradient-to-r from-blue-600 to-indigo-700 border-b">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3 text-white">
                  <span className="text-2xl">🧮</span>
                  <div>
                    <p className="font-semibold text-lg">Need to estimate your Microsoft security costs?</p>
                    <p className="text-white/80 text-sm">Use our free pricing calculator with automatic SKU overlap detection</p>
                  </div>
                </div>
                <Link
                  href="/tools/planning/microsoft-security-quote-builder"
                  className="px-6 py-3 bg-white text-blue-700 font-semibold rounded-lg hover:bg-blue-50 transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  Open Pricing Calculator
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* ===== 4. AVAILABLE PLANS + QUOTE FORM (Side by Side) ===== */}
        <section id="inquiry-form" className="py-12 bg-gray-50 border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              {/* Left Column - Available Plans */}
              <div className="min-h-[500px] flex flex-col">
                {variants.length > 0 ? (
                  <>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-bold text-gray-900">
                        Available Plans
                      </h2>
                      {hasMultipleVariants && (
                        <Link
                          href={`/software/${family.slug}/compare`}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                        >
                          Compare Plans <ArrowRight className="w-4 h-4" />
                        </Link>
                      )}
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm flex-1">
                      <VariantCards
                        variants={variants}
                        selectedVariantId={selectedVariant?.id}
                        onSelectVariant={setSelectedVariant}
                        selectedAddonIds={selectedAddonIds}
                        onToggleAddon={handleToggleAddon}
                        showSelection={true}
                      />
                    </div>
                  </>
                ) : (
                  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm flex-1 flex flex-col justify-center">
                    <Package className="w-14 h-14 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">Custom Pricing Available</h3>
                    <p className="text-gray-600 text-center text-base">
                      Contact us for availability information tailored to your needs.
                    </p>
                  </div>
                )}
              </div>

              {/* Right Column - Quote Request Form */}
              <div className="min-h-[500px]">
                <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-gray-200 lg:sticky lg:top-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Get Your Price
                  </h2>
                  <p className="text-gray-600 mb-6">
                    Tell us what you need and we&apos;ll send you a custom quote within 1 business day.
                  </p>
                  <SoftwareQuoteForm
                    productName={family.name}
                    productSlug={family.slug}
                    selectedVariantName={selectedVariant?.displayName}
                    selectedAddonNames={selectedAddonNames}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== 6. WHY INVENTIVE HQ ===== */}
        <WhyInventiveHQ productName={family.name} />

        {/* ===== 4. IDEAL FOR / USE CASES ===== */}
        {useCases.length > 0 && (
          <UseCaseCards useCases={useCases} />
        )}

        {/* ===== 5. ABOUT SECTION (Long Description for SEO) ===== */}
        {family.longDescription && (
          <section className="py-16 bg-white border-t">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                {/* Left column - heading and intro */}
                <div className="lg:col-span-4">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    About {family.name}
                  </h2>
                  <p className="text-gray-500 text-sm">
                    Learn how {family.name} from {family.vendorName} can help transform your business operations.
                  </p>
                </div>
                {/* Right column - content with visual treatment */}
                <div className="lg:col-span-8">
                  <div className="prose prose-gray max-w-none">
                    <ReactMarkdown
                      components={{
                        img: ({ src, alt }) => (
                          <img
                            src={src}
                            alt={alt || ''}
                            className="w-full rounded-lg shadow-md my-6"
                          />
                        ),
                        p: ({ children }) => (
                          <p className="text-gray-600 leading-relaxed mb-4">{children}</p>
                        ),
                        strong: ({ children }) => (
                          <strong className="text-gray-900 font-semibold">{children}</strong>
                        ),
                        ul: ({ children }) => (
                          <ul className="space-y-2 my-4">{children}</ul>
                        ),
                        li: ({ children }) => (
                          <li className="flex gap-3 text-gray-600">
                            <span className="flex-shrink-0 w-1.5 h-1.5 bg-blue-500 rounded-full mt-2" />
                            <span>{children}</span>
                          </li>
                        ),
                      }}
                    >
                      {family.longDescription}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ===== 6. FAQ SECTION ===== */}
        {faqs.length > 0 && (
          <section className="py-12 bg-white">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                Frequently Asked Questions
              </h2>
              <div className="bg-gray-50 rounded-xl p-6">
                {faqs.map((faq, index) => (
                  <FAQItem
                    key={faq.id}
                    faq={faq}
                    isOpen={openFaqIndex === index}
                    onToggle={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ===== 7. CTA SECTION ===== */}
        <section className="bg-gradient-to-r from-[#1a6f8f] to-[#1e40af] py-12 sm:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Get Started with {family.name}?
            </h2>
            <p className="text-white/90 text-base sm:text-lg mb-8 sm:mb-10 max-w-2xl mx-auto">
              Let our experts help you deploy and configure {family.name} for your organization. Get expert guidance and dedicated support.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={scrollToForm}
                className="px-8 sm:px-10 py-4 min-h-[52px] bg-white text-blue-600 font-bold rounded-lg hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl text-base sm:text-lg"
              >
                Get Your Custom Quote
              </button>
              <Link
                href={contactUrl}
                className="px-6 sm:px-8 py-4 min-h-[52px] bg-transparent text-white font-medium rounded-lg border-2 border-white/50 hover:bg-white/10 hover:border-white transition-all flex items-center justify-center text-base"
              >
                Have Questions? Talk to Us
              </Link>
            </div>
          </div>
        </section>

        {/* ===== 10. SIMILAR PRODUCTS ===== */}
        {similarProducts.length > 0 && (
          <section className="py-10 sm:py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 sm:mb-8 text-center">
                Similar Products
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                {similarProducts.slice(0, 3).map((product) => (
                  <Link
                    key={product.slug}
                    href={`/software/${product.slug}`}
                    className="bg-gray-50 rounded-xl border border-gray-200 p-5 sm:p-6 hover:border-blue-300 hover:shadow-md transition-all group"
                  >
                    <div className="flex items-start gap-3 sm:gap-4">
                      {product.iconUrl ? (
                        <img
                          src={product.iconUrl}
                          alt={`${product.name} icon`}
                          className="w-12 h-12 rounded-lg object-contain bg-white p-1 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-lg font-bold text-white">
                            {product.name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-500 mb-1">{product.vendorName}</p>
                        <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors text-base">
                          {product.name}
                        </h3>
                        {product.tagline && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {product.tagline}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="mt-4 text-sm text-blue-600 font-medium flex items-center gap-1 group-hover:gap-2 transition-all min-h-[44px] py-2">
                      View Product <ArrowRight className="w-5 h-5" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ===== 11. RECENTLY VIEWED ===== */}
        <section className="py-12 bg-gray-50 border-t">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <RecentlyViewedCompact currentSlug={family.slug} maxItems={4} />
          </div>
        </section>
      </div>

      {/* ===== STICKY FLOATING CTA ===== */}
      {showStickyCta && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.1)] py-3 px-4 sm:px-6 lg:hidden">
          <button
            onClick={scrollToForm}
            className="w-full px-6 py-3.5 bg-amber-400 hover:bg-amber-300 text-gray-900 font-bold rounded-lg transition-colors shadow-md text-base"
          >
            Get My Price
          </button>
        </div>
      )}
      {showStickyCta && (
        <button
          onClick={scrollToForm}
          className="hidden lg:flex fixed bottom-8 left-8 z-40 items-center gap-2 px-6 py-3.5 bg-amber-400 hover:bg-amber-300 text-gray-900 font-bold rounded-full transition-all shadow-lg hover:shadow-xl text-base"
        >
          <ArrowRight className="w-5 h-5" />
          Get My Price
        </button>
      )}
    </>
  );
}

export type { SoftwareProductLayoutProps };
